import React, { useState, useEffect } from 'react';
import { PTOFormula, PTOFormulaFormData, AuthUser } from '../types';
import { 
  getAllPTOFormulas, 
  createPTOFormula, 
  updatePTOFormula, 
  deletePTOFormula,
  calculateAllUsersPTO
} from '../firebase/firestore';

interface AdminPTOFormulasProps {
  currentUser: AuthUser;
}

const AdminPTOFormulas: React.FC<AdminPTOFormulasProps> = ({ currentUser }) => {
  const [formulas, setFormulas] = useState<PTOFormula[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFormula, setEditingFormula] = useState<PTOFormula | null>(null);
  const [calculating, setCalculating] = useState(false);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState<PTOFormulaFormData>({
    name: '',
    description: '',
    minYearsOfService: 0,
    maxYearsOfService: undefined,
    ptoDays: 14,
    minAge: undefined,
    maxAge: undefined,
    isActive: true
  });

  useEffect(() => {
    loadFormulas();
  }, []);

  const loadFormulas = async () => {
    try {
      setLoading(true);
      const formulasData = await getAllPTOFormulas();
      setFormulas(formulasData);
    } catch (error) {
      console.error('Error loading PTO formulas:', error);
      setError('Formüller yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'maxYearsOfService' || name === 'minAge' || name === 'maxAge') {
      // Handle optional number fields
      const numValue = value === '' ? undefined : parseInt(value);
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else {
      const numValue = name === 'minYearsOfService' || name === 'ptoDays' ? parseInt(value) : value;
      setFormData(prev => ({ ...prev, [name]: numValue }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.name.trim()) {
      setError('Formül adı gereklidir');
      return;
    }
    if (!formData.description.trim()) {
      setError('Açıklama gereklidir');
      return;
    }
    if (formData.minYearsOfService < 0) {
      setError('Minimum hizmet yılı 0\'dan küçük olamaz');
      return;
    }
    if (formData.maxYearsOfService !== undefined && formData.maxYearsOfService <= formData.minYearsOfService) {
      setError('Maksimum hizmet yılı minimum hizmet yılından büyük olmalıdır');
      return;
    }
    if (formData.ptoDays < 0) {
      setError('İzin günü 0\'dan küçük olamaz');
      return;
    }
    if (formData.minAge !== undefined && formData.maxAge !== undefined && formData.minAge > formData.maxAge) {
      setError('Minimum yaş maksimum yaştan büyük olamaz');
      return;
    }

    try {
      if (editingFormula) {
        await updatePTOFormula(editingFormula.id, formData);
        setSuccess('Formül başarıyla güncellendi');
      } else {
        await createPTOFormula(formData, currentUser.uid);
        setSuccess('Formül başarıyla oluşturuldu');
      }
      
      setShowForm(false);
      setEditingFormula(null);
      resetForm();
      loadFormulas();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving PTO formula:', error);
      setError('Formül kaydedilirken hata oluştu');
    }
  };

  const handleEdit = (formula: PTOFormula) => {
    setEditingFormula(formula);
    setFormData({
      name: formula.name,
      description: formula.description,
      minYearsOfService: formula.minYearsOfService,
      maxYearsOfService: formula.maxYearsOfService,
      ptoDays: formula.ptoDays,
      minAge: formula.minAge,
      maxAge: formula.maxAge,
      isActive: formula.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (formulaId: string) => {
    if (!window.confirm('Bu formülü silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await deletePTOFormula(formulaId);
      setSuccess('Formül başarıyla silindi');
      loadFormulas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting PTO formula:', error);
      setError('Formül silinirken hata oluştu');
    }
  };

  const handleCalculateAll = async () => {
    if (!window.confirm('Tüm kullanıcıların yıllık izin değerlerini yeniden hesaplamak istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setCalculating(true);
      await calculateAllUsersPTO();
      setSuccess('Tüm kullanıcıların yıllık izin değerleri başarıyla hesaplandı');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error calculating all users PTO:', error);
      setError('Yıllık izin hesaplama sırasında hata oluştu');
    } finally {
      setCalculating(false);
    }
  };

  

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      minYearsOfService: 0,
      maxYearsOfService: undefined,
      ptoDays: 14,
      minAge: undefined,
      maxAge: undefined,
      isActive: true
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingFormula(null);
    resetForm();
    setError('');
  };

  const getYearsOfServiceText = (formula: PTOFormula) => {
    if (formula.maxYearsOfService === undefined) {
      return `${formula.minYearsOfService} yıl ve üzeri`;
    }
    return `${formula.minYearsOfService} - ${formula.maxYearsOfService} yıl`;
  };

  const getAgeText = (formula: PTOFormula) => {
    if (formula.minAge === undefined && formula.maxAge === undefined) {
      return 'Tüm yaşlar';
    }
    if (formula.minAge === undefined) {
      return `${formula.maxAge} yaş ve altı`;
    }
    if (formula.maxAge === undefined) {
      return `${formula.minAge} yaş ve üzeri`;
    }
    return `${formula.minAge} - ${formula.maxAge} yaş`;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Formüller yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">Yıllık İzin Hesaplama Formülleri</h2>
            <p className="text-sm text-gray-600">Kullanıcıların yıllık izin haklarını hesaplamak için formüller tanımlayın</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleCalculateAll}
              disabled={calculating}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {calculating ? 'Hesaplanıyor...' : 'Tüm Yıllık İzinleri Hesapla'}
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Yeni Formül Ekle
            </button>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Formula Form */}
      {showForm && (
        <div className="mb-6 bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingFormula ? 'Formülü Düzenle' : 'Yeni Formül Ekle'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Formül Adı *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Örn: 1-5 Yıl Deneyim"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İzin Günü *
                </label>
                <input
                  type="number"
                  name="ptoDays"
                  value={formData.ptoDays}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Hizmet Yılı *
                </label>
                <input
                  type="number"
                  name="minYearsOfService"
                  value={formData.minYearsOfService}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maksimum Hizmet Yılı
                </label>
                <input
                  type="number"
                  name="maxYearsOfService"
                  value={formData.maxYearsOfService || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  placeholder="Boş bırakılırsa üst sınır yok"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Yaş
                </label>
                <input
                  type="number"
                  name="minAge"
                  value={formData.minAge || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  placeholder="Boş bırakılırsa yaş sınırı yok"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maksimum Yaş
                </label>
                <input
                  type="number"
                  name="maxAge"
                  value={formData.maxAge || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  placeholder="Boş bırakılırsa yaş sınırı yok"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Açıklama *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Formülün açıklaması"
                required
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Aktif (Bu formül yıllık izin hesaplamalarında kullanılır)
              </label>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {editingFormula ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulas List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {formulas.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Henüz formül yok</h3>
            <p className="text-sm text-gray-500">Yıllık izin hesaplama formülleri eklemek için "Yeni Formül Ekle" butonuna tıklayın</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Formül Adı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hizmet Yılı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Yaş Aralığı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İzin Günü
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {formulas.map((formula) => (
                  <tr key={formula.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{formula.name}</div>
                        <div className="text-sm text-gray-500">{formula.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getYearsOfServiceText(formula)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getAgeText(formula)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formula.ptoDays} gün
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formula.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Aktif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Pasif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(formula)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Düzenle
                        </button>
                        <button
                          onClick={() => handleDelete(formula.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Example Rules */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Örnek Formül Kuralları:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• 1-5 yıl hizmet: 14 gün izin</li>
          <li>• 5-15 yıl hizmet: 20 gün izin</li>
          <li>• 15+ yıl hizmet: 26 gün izin</li>
          <li>• 18 yaş altı veya 50+ yaş: minimum 20 gün izin</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminPTOFormulas;
